
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function TableViewPage() {
  useEffect(() => {
    document.title = 'Table View Painters Cape Town | Residential Commercial Painting | Coastal Protection';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Table View, Cape Town. Expert residential and commercial painting with coastal protection. Quality workmanship for Table View properties.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Table View painters, coastal painting Cape Town, residential commercial painting');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/table-view`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Table View Painters Cape Town",
      "description": "Professional painting services in Table View, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/table-view`,
      "areaServed": {
        "@type": "Place",
        "name": "Table View, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.8417,
          "longitude": 18.4906
        },
        "geoRadius": "12000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Table View Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f9d995900b3077df8a3c1e915a560b87.jpg"
            alt="Professional painting services in Table View, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Table View Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Expert painting services for Table View's diverse residential and commercial properties. Quality craftsmanship with coastal protection and stunning Table Mountain views.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Complete Painting Services in Table View
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From family homes to commercial developments, we provide comprehensive painting solutions for all property types in Table View's thriving coastal community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Residential Painting',
                description: 'Complete home painting services for Table View families, from interior rooms to exterior facades with coastal protection.',
                features: ['Interior painting', 'Exterior painting', 'Coastal protection', 'Color consultation']
              },
              {
                icon: 'ri-building-line',
                title: 'Commercial Properties',
                description: 'Professional commercial painting for offices, retail spaces, and business facilities in Table View commercial areas.',
                features: ['Office buildings', 'Retail centers', 'Business parks', 'Industrial facilities']
              },
              {
                icon: 'ri-community-line',
                title: 'Residential Developments',
                description: 'Specialized painting services for apartment complexes, townhouse developments, and residential estates.',
                features: ['Apartment complexes', 'Townhouse estates', 'Gated communities', 'Common facilities']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Table View Residents Choose Our Painting Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Local Table View Knowledge',
                    description: 'Deep understanding of Table View\'s coastal conditions, architectural styles, and community needs.'
                  },
                  {
                    title: 'Coastal Protection Expertise',
                    description: 'Specialized knowledge of coastal painting requirements with marine-grade materials for lasting protection.'
                  },
                  {
                    title: 'Community Focused',
                    description: 'Committed to serving the Table View community with reliable, professional service and local expertise.'
                  },
                  {
                    title: 'Quality Assurance',
                    description: 'Premium materials and skilled craftsmanship ensuring beautiful, long-lasting results for your property.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Table%20View%20residential%20property%2C%20coastal%20suburb%20home%20painting%2C%20skilled%20painting%20team%20with%20Table%20Mountain%20backdrop%2C%20quality%20painting%20services%20Cape%20Town&width=600&height=700&seq=table-view-team-work&orientation=portrait"
                alt="Professional painting team in Table View"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Table View Painting Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our latest completed projects in Table View, showcasing our versatility and commitment to quality across different property types.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Contemporary Family Home',
                location: 'Table View Heights',
                description: 'Complete interior and exterior painting of a modern 5-bedroom family home with mountain views.',
                image: 'https://readdy.ai/api/search-image?query=Contemporary%20family%20home%20painting%20in%20Table%20View%20Heights%20Cape%20Town%2C%20modern%20residential%20architecture%20with%20Table%20Mountain%20views%2C%20professional%20exterior%20and%20interior%20painting&width=400&height=300&seq=table-view-project-1&orientation=landscape'
              },
              {
                title: 'Business Park Complex',
                location: 'Table View Commercial',
                description: 'Comprehensive painting renovation for a multi-tenant business park and office complex.',
                image: 'https://readdy.ai/api/search-image?query=Business%20park%20complex%20painting%20in%20Table%20View%2C%20commercial%20office%20buildings%2C%20modern%20business%20premises%20painting%2C%20professional%20commercial%20painting%20Cape%20Town&width=400&height=300&seq=table-view-project-2&orientation=landscape'
              },
              {
                title: 'Coastal Apartment Estate',
                location: 'Table View Beachfront',
                description: 'Exterior painting and waterproofing of a 20-unit beachfront apartment development.',
                image: 'https://readdy.ai/api/search-image?query=Coastal%20apartment%20estate%20painting%20in%20Table%20View%20beachfront%2C%20residential%20complex%20with%20ocean%20proximity%2C%20apartment%20building%20exterior%20painting%2C%20coastal%20property%20maintenance%20Cape%20Town&width=400&height=300&seq=table-view-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Table View Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Read testimonials from satisfied customers across Table View and surrounding areas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Rachel Adams',
                location: 'Table View Heights',
                rating: 5,
                text: 'Excellent work on our family home. The coastal protection they applied has kept our exterior looking fresh despite the sea air. Professional and reliable service.'
              },
              {
                name: 'Kevin Daniels',
                location: 'Table View Commercial',
                rating: 5,
                text: 'Outstanding commercial painting for our business park. They coordinated perfectly with multiple tenants and delivered exceptional results on time.'
              },
              {
                name: 'Amanda Foster',
                location: 'Table View Beachfront',
                rating: 5,
                text: 'The waterproofing and painting of our apartment complex was handled professionally. Great communication and quality workmanship throughout the project.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Transform Your Table View Property Today
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a free consultation and detailed quote. Experience the difference of professional painting services tailored to Table View properties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
